import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useDataSourceQuery } from '@/hooks/useDataSources'
import { AgentProxyService } from '@/services/agentProxyService'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import {
  CogIcon,
  ChartBarIcon,
  WrenchIcon,
  GlobeAltIcon,
  PuzzlePieceIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  TableCellsIcon,
  ArrowPathRoundedSquareIcon
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import Fuse from 'fuse.js'

type SystemInfoData = Record<string, { 
  columns: string[];
  rows: Record<string, string>[];
}>

interface SystemMenuItem {
  id: string
  name: string
  icon: React.ComponentType<{ className?: string }>
  query: string
  description: string
}

// Define system information categories based on database type
const getSystemMenuItems = (databaseType?: string): SystemMenuItem[] => {
  if (databaseType === 'MySQL') {
    return [
      {
        id: 'global-variables',
        name: 'Global Variables',
        icon: CogIcon,
        query: 'SHOW GLOBAL VARIABLES',
        description: 'MySQL server global variables and their values'
      },
      {
        id: 'session-variables',
        name: 'Session Variables',
        icon: CogIcon,
        query: 'SHOW SESSION VARIABLES',
        description: 'Current session variables and their values'
      },
      {
        id: 'global-status',
        name: 'Global Status',
        icon: ChartBarIcon,
        query: 'SHOW GLOBAL STATUS',
        description: 'Server status variables and statistics'
      },
      {
        id: 'engines',
        name: 'Storage Engines',
        icon: WrenchIcon,
        query: 'SHOW ENGINES',
        description: 'Available storage engines and their status'
      },
      {
        id: 'database-statistics',
        name: 'Database Statistics',
        icon: TableCellsIcon,
        query: `SELECT 
                  'Database Sizes' AS category,
                  table_schema AS name,
                  CONCAT(ROUND(SUM(data_length + index_length) / 1024 / 1024, 2), ' MB') AS value,
                  'Total size including data and indexes' AS description
                FROM information_schema.tables 
                WHERE table_schema NOT IN ('information_schema', 'performance_schema', 'mysql', 'sys')
                GROUP BY table_schema
                UNION ALL
                SELECT 
                  'Connection Stats' AS category,
                  'Total Connections' AS name,
                  variable_value AS value,
                  'Total number of connections since server start' AS description
                FROM performance_schema.global_status 
                WHERE variable_name = 'Connections'
                UNION ALL
                SELECT 
                  'Connection Stats' AS category,
                  'Current Connections' AS name,
                  variable_value AS value,
                  'Currently active connections' AS description
                FROM performance_schema.global_status 
                WHERE variable_name = 'Threads_connected'
                UNION ALL
                SELECT 
                  'Query Stats' AS category,
                  'Total Questions' AS name,
                  variable_value AS value,
                  'Total number of statements executed' AS description
                FROM performance_schema.global_status 
                WHERE variable_name = 'Questions'
                UNION ALL
                SELECT 
                  'Transaction Stats' AS category,
                  'Commits' AS name,
                  variable_value AS value,
                  'Total number of committed transactions' AS description
                FROM performance_schema.global_status 
                WHERE variable_name = 'Com_commit'
                ORDER BY category, name`,
        description: 'Database sizes, connection metrics, and transaction statistics'
      },
      {
        id: 'replication-status',
        name: 'Replication Status',
        icon: ArrowPathRoundedSquareIcon,
        query: `SELECT 
                  'Replication Settings' AS category,
                  'Server ID' AS name,
                  variable_value AS value,
                  'Unique server identifier for replication' AS description
                FROM performance_schema.global_variables 
                WHERE variable_name = 'server_id'
                UNION ALL
                SELECT 
                  'Replication Settings' AS category,
                  'Binary Logging' AS name,
                  variable_value AS value,
                  'Whether binary logging is enabled' AS description
                FROM performance_schema.global_variables 
                WHERE variable_name = 'log_bin'
                UNION ALL
                SELECT 
                  'Replication Settings' AS category,
                  'Read Only' AS name,
                  variable_value AS value,
                  'Whether server is in read-only mode' AS description
                FROM performance_schema.global_variables 
                WHERE variable_name = 'read_only'
                UNION ALL
                SELECT 
                  'Server Status' AS category,
                  'Uptime' AS name,
                  CONCAT(FLOOR(variable_value / 86400), ' days, ', 
                         FLOOR((variable_value % 86400) / 3600), ' hours') AS value,
                  'Server uptime since last restart' AS description
                FROM performance_schema.global_status 
                WHERE variable_name = 'Uptime'
                UNION ALL
                SELECT 
                  'Binlog Status' AS category,
                  'Binlog Cache Use' AS name,
                  variable_value AS value,
                  'Number of transactions that used the binary log cache' AS description
                FROM performance_schema.global_status 
                WHERE variable_name = 'Binlog_cache_use'`,
        description: 'MySQL replication configuration and status information'
      }
    ]
  } else if (databaseType === 'PostgreSQL') {
    return [
      {
        id: 'settings',
        name: 'Settings',
        icon: CogIcon,
        query: `SELECT name as variable_name, setting as value, COALESCE(short_desc, 'No description available') as description 
                 FROM pg_settings 
                 ORDER BY category, name`,
        description: 'PostgreSQL configuration settings'
      },
      {
        id: 'collations',
        name: 'Collations',
        icon: GlobeAltIcon,
        query: `SELECT collname as name, collctype as lc_ctype, collcollate as lc_collate 
                 FROM pg_collation 
                 ORDER BY collname`,
        description: 'Available collations for text sorting and comparison'
      },
      {
        id: 'available-extensions',
        name: 'Available Extensions',
        icon: PuzzlePieceIcon,
        query: `SELECT name, default_version, installed_version, comment as description 
                 FROM pg_available_extensions 
                 ORDER BY name`,
        description: 'Extensions available for installation'
      },
      {
        id: 'encodings',
        name: 'Character Encodings',
        icon: DocumentTextIcon,
        query: `SELECT pg_encoding_to_char(conforencoding) as encoding_name,
                        conname as conversion_name
                 FROM pg_conversion 
                 ORDER BY encoding_name, conversion_name`,
        description: 'Available character encodings and conversions'
      },
      {
        id: 'database-statistics',
        name: 'Database Statistics',
        icon: TableCellsIcon,
        query: `SELECT 
                  'Database Sizes' AS category,
                  datname AS name,
                  pg_size_pretty(pg_database_size(datname)) AS value,
                  'Total database size including all objects' AS description
                FROM pg_database 
                WHERE datistemplate = false
                UNION ALL
                SELECT 
                  'Connection Stats' AS category,
                  datname AS name,
                  COALESCE(numbackends::text, '0') AS value,
                  'Current active connections to database' AS description
                FROM pg_stat_database 
                WHERE datname IS NOT NULL
                UNION ALL
                SELECT 
                  'Transaction Stats' AS category,
                  datname AS name,
                  CONCAT(xact_commit, ' commits, ', xact_rollback, ' rollbacks') AS value,
                  'Total committed and rolled back transactions' AS description
                FROM pg_stat_database 
                WHERE datname IS NOT NULL AND datname NOT LIKE 'template%'
                UNION ALL
                SELECT 
                  'Query Stats' AS category,
                  datname AS name,
                  CONCAT(tup_returned, ' returned, ', tup_fetched, ' fetched') AS value,
                  'Tuples returned and fetched from database' AS description
                FROM pg_stat_database 
                WHERE datname IS NOT NULL AND datname NOT LIKE 'template%'
                ORDER BY category, name`,
        description: 'Database sizes, connection metrics, and transaction statistics'
      },
      {
        id: 'replication-status',
        name: 'Replication Status',
        icon: ArrowPathRoundedSquareIcon,
        query: `SELECT 
                  'Server Info' AS category,
                  'PostgreSQL Version' AS name,
                  version() AS value,
                  'Current PostgreSQL server version' AS description
                UNION ALL
                SELECT 
                  'Server Info' AS category,
                  'Server Start Time' AS name,
                  pg_postmaster_start_time()::text AS value,
                  'When the PostgreSQL server was started' AS description
                UNION ALL
                SELECT 
                  'WAL Settings' AS category,
                  'WAL Level' AS name,
                  setting AS value,
                  'Write-Ahead Logging level configuration' AS description
                FROM pg_settings 
                WHERE name = 'wal_level'
                UNION ALL
                SELECT 
                  'WAL Settings' AS category,
                  'Max WAL Senders' AS name,
                  setting AS value,
                  'Maximum number of WAL sender processes' AS description
                FROM pg_settings 
                WHERE name = 'max_wal_senders'
                UNION ALL
                SELECT 
                  'Replication Status' AS category,
                  'Active Replicas' AS name,
                  COALESCE(COUNT(*)::text, '0') AS value,
                  'Number of currently connected replica servers' AS description
                FROM pg_stat_replication
                UNION ALL
                SELECT 
                  'Recovery Status' AS category,
                  'In Recovery' AS name,
                  pg_is_in_recovery()::text AS value,
                  'Whether server is currently in recovery mode' AS description`,
        description: 'PostgreSQL replication, WAL, and recovery status information'
      }
    ]
  }
  return []
}

export function SystemInformation() {
  const { connectionId } = useParams<{ connectionId: string }>()
  const { data: dataSource, isLoading: isLoadingConnection } = useDataSourceQuery(connectionId || '')
  const { showToast } = useToast()
  
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [data, setData] = useState<SystemInfoData>({})
  const [loading, setLoading] = useState<Record<string, boolean>>({})
  const [error, setError] = useState<string | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const fetchingRef = useRef<Set<string>>(new Set())
  const dataRef = useRef<SystemInfoData>({})

  const databaseType = dataSource?.databaseType

  const systemMenuItems = useMemo(() => getSystemMenuItems(databaseType), [databaseType])

  // Apply fuzzy search if there's a search query
  const currentData = useMemo(() => {
    const rawData = data[selectedCategory]
    
    if (!searchQuery.trim() || !rawData || rawData.rows.length === 0) {
      return rawData
    }
    
    // Create dynamic search keys for all columns
    const searchKeys = rawData.columns.map(column => column)
    
    const fuseOptions = {
      keys: searchKeys,
      threshold: 0.3, // 0.0 = exact match, 1.0 = matches anything
      includeScore: true,
      minMatchCharLength: 1,
      ignoreLocation: true,
      shouldSort: true,
    }
    
    const fuse = new Fuse(rawData.rows, fuseOptions)
    const searchResults = fuse.search(searchQuery)
    // Extract the original items from Fuse results and sort by relevance score
    const filteredRows = searchResults.map(result => result.item)
    
    return {
      columns: rawData.columns,
      rows: filteredRows
    }
  }, [data, selectedCategory, searchQuery])

  // Search handlers
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
  }, [])

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  // Set initial category when menu items are available
  useEffect(() => {
    if (systemMenuItems.length > 0 && !selectedCategory) {
      setSelectedCategory(systemMenuItems[0].id)
    }
  }, [systemMenuItems, selectedCategory])

  // Fetch data when category changes or refresh is triggered
  useEffect(() => {
    if (!selectedCategory || !connectionId) return
    
    const menuItem = systemMenuItems.find(item => item.id === selectedCategory)
    if (!menuItem) return
    
    // For refresh, always fetch. For initial load, check if already exists or is being fetched
    const shouldSkip = refreshTrigger === 0 && fetchingRef.current.has(selectedCategory)
    if (shouldSkip) return
    
    // Also skip if data already exists and this isn't a refresh
    const hasExistingData = dataRef.current[selectedCategory] && dataRef.current[selectedCategory].rows && dataRef.current[selectedCategory].rows.length > 0
    if (refreshTrigger === 0 && hasExistingData) return

    // Directly call the fetch logic here to avoid dependency issues
    const fetchData = async () => {
      fetchingRef.current.add(selectedCategory)
      setLoading(prev => ({ ...prev, [selectedCategory]: true }))
      setError(null)

      try {
        // For MySQL system queries (SHOW commands), no specific database is needed
        // For PostgreSQL queries, use defaultDatabase or fall back to 'postgres'
        const databaseName = databaseType === 'MySQL' 
          ? undefined 
          : (dataSource?.defaultDatabase || 'postgres')

        const response = await AgentProxyService.executeQuery(
          connectionId,
          menuItem.query,
          databaseName
        )

        if (response && typeof response === 'object') {
          const responseObj = response as Record<string, unknown>
          
          // Check if response has the expected structure
          if (responseObj.status === 'success' || responseObj.success) {
            // The response structure appears to be direct (columns and rows at root level)
            const columns = responseObj.columns as string[] | undefined
            const rows = responseObj.rows as unknown[][] | undefined
            
            if (columns && rows && rows.length > 0) {
              // Transform the data to preserve dynamic columns
              const transformedRows = rows.map((row: unknown[]) => {
                const rowObject: Record<string, string> = {}
                columns.forEach((column: string, index: number) => {
                  const value = row[index]
                  rowObject[column] = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' 
                    ? String(value) 
                    : value === null || value === undefined
                    ? '' 
                    : JSON.stringify(value)
                })
                return rowObject
              })

              setData(prev => ({
                ...prev,
                [selectedCategory]: {
                  columns: columns,
                  rows: transformedRows
                }
              }))
              setError(null) // Clear any previous errors
            } else {
              throw new Error('No data returned from query')
            }
          } else {
            throw new Error((responseObj.message as string) || 'Query failed')
          }
        } else {
          throw new Error('Invalid response format')
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
        setError(errorMessage)
        showToast('error', errorMessage)
      } finally {
        setLoading(prev => ({ ...prev, [selectedCategory]: false }))
        fetchingRef.current.delete(selectedCategory)
      }
    }

    fetchData()
  }, [selectedCategory, connectionId, databaseType, dataSource?.defaultDatabase, systemMenuItems, showToast, refreshTrigger])

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId)
    setSearchQuery('') // Clear search when switching categories
  }

  const handleRefresh = () => {
    if (selectedCategory) {
      // Clear existing data and fetching state to force refresh
      setData(prev => {
        const { [selectedCategory]: _, ...rest } = prev
        return rest
      })
      fetchingRef.current.delete(selectedCategory)
      // Trigger refresh by incrementing the trigger
      setRefreshTrigger(prev => prev + 1)
    }
  }

  if (isLoadingConnection) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner />
        <span className="ml-3 text-gray-500 dark:text-gray-400">
          Loading connection...
        </span>
      </div>
    )
  }

  if (!dataSource) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Connection Selected
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            Please select a database connection to view system information.
          </p>
        </div>
      </div>
    )
  }

  if (systemMenuItems.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Unsupported Database Type
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            System information is not available for {databaseType} databases.
          </p>
        </div>
      </div>
    )
  }

  const selectedMenuItem = systemMenuItems.find(item => item.id === selectedCategory)
  const rawData = data[selectedCategory]
  
  const isLoading = loading[selectedCategory] || false

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-4">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            System Information
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {dataSource.displayName} ({databaseType})
          </p>
        </div>
        
        {/* Horizontal Navigation Tabs */}
        <div className="overflow-x-auto">
          <nav className="flex space-x-1">
            {systemMenuItems.map((item) => {
              const isSelected = item.id === selectedCategory
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => handleCategorySelect(item.id)}
                  className={clsx(
                    'flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
                    isSelected
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                  )}
                  title={item.description}
                >
                  <item.icon className={clsx(
                    'w-4 h-4 mr-2 flex-shrink-0',
                    isSelected
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-400'
                  )} />
                  <span>{item.name}</span>
                </button>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Content Section with Search and Actions */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {selectedMenuItem && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedMenuItem.name}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {selectedMenuItem.description}
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {/* Search Input - Only show if there's data */}
            {rawData && rawData.rows.length > 0 && (
              <div className="relative w-64">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  placeholder="Search ..."
                  className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  onChange={(e) => { handleSearchChange(e.target.value) }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Clear search"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Refresh
            </button>
          </div>
        </div>
        
        {/* Search Results Summary */}
        {searchQuery && rawData && rawData.rows.length > 0 && (
          <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
            {!currentData || currentData.rows.length === 0 ? (
              <span>No results found for "{searchQuery}"</span>
            ) : (
              <span>
                Found {currentData.rows.length} result{currentData.rows.length !== 1 ? 's' : ''} 
                {currentData.rows.length > 0 && ` matching "${searchQuery}"`}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    Error loading system information
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
              <span className="ml-3 text-gray-500 dark:text-gray-400">
                Loading system information...
              </span>
            </div>
          ) : currentData && currentData.rows.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      {currentData.columns.map((column, index) => (
                        <th 
                          key={index}
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          {column.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {currentData.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        {currentData.columns.map((column, colIndex) => (
                          <td 
                            key={colIndex}
                            className="px-3 py-2 text-xs text-gray-900 dark:text-white break-words leading-tight"
                          >
                            <div className="max-w-md overflow-hidden">
                              {row[column]}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              {searchQuery && rawData && rawData.rows.length > 0 ? (
                <>
                  <MagnifyingGlassIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No matching results
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    No system information matches "{searchQuery}". Try adjusting your search terms.
                  </p>
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md shadow-sm transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 mr-2" />
                    Clear Search
                  </button>
                </>
              ) : (
                <>
                  <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No Data Available
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Click Refresh to load system information.
                  </p>
                </>
              )}
            </div>
          )}
      </div>
    </div>
  )
}